// React Imports
import { Fragment, ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import {
  Button,
  Form,
  Select,
  Switch,
  Upload,
  UploadProps,
  message,
} from "antd";
import {
  InboxOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";

// Style Imports
import "./add-edit-slider-screen.scss";

// Local Imports
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import Label from "../../components/label/label.component";
import CustomInput from "../../components/custom-input/custom-input.component";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const { Option } = Select;

const { Dragger } = Upload;

const props: UploadProps = {
  name: "file",
  multiple: true,
  action: "https://run.mocky.io/v3/435e224c-44fb-4773-9faf-380c5e6a2188",
  onChange(info) {
    const { status } = info.file;
    if (status !== "uploading") {
      console.log(info.file, info.fileList);
    }
    if (status === "done") {
      message.success(`${info.file.name} file uploaded successfully.`);
    } else if (status === "error") {
      message.error(`${info.file.name} file upload failed.`);
    }
  },
  onDrop(e) {
    console.log("Dropped files", e.dataTransfer.files);
  },
};

const AddEditSliderScreen: React.FC = (): ReactElement => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [submitForm, setSubmitForm] = useState(false);

  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmitForm(true);
      },
      () => {
        setSubmitForm(false);
      }
    );
  }, [values]);

  const beforeUpload = (file) => {
    const isImageOrPdf =
      file.type === "image/png" ||
      file.type === "image/gif" ||
      file.type === "image/jpg" ||
      file.type === "image/jpeg" ||
      file.type === "application/pdf";

    if (!isImageOrPdf) {
      toast.error(
        "Error: Only PDF and image files are allowed for upload. Please try again with a valid file format!"
      );
    }

    if (!isImageOrPdf) {
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  return (
    <section className="add-edit-slider-container flex flex-col">
      <BreadcrumbComponent />

      <div className="flex flex-col py-[2%] bg-white rounded-[20px]">
        <h3 className=" text-xl border-b-[1px] border-black-200 pt-1 px-[2%] pb-4">
          Create a splash screen
        </h3>
        <Form form={form} className=" p-[2%]">
          <div className="flex flex-col justify-between w-full">
            <Form.List
              name="sliders"
              initialValue={[
                {
                  title: "",
                  action: "",
                  order: null,
                  isActive: true,
                },
              ]}
              rules={[
                {
                  validator: async (_, names) => {
                    if (!names) {
                      return Promise.reject(new Error("At least 1 screen"));
                    }
                  },
                },
              ]}
            >
              {(fields: any = 1, { add, remove }, { errors }) => (
                <Form.Item>
                  <div className="flex flex-col w-full">
                    {fields.map((field: any, index: any) => (
                      <Fragment key={index}>
                        <div className="flex field-group-container flex-wrap justify-between mb-4">
                          <div className="field flex flex-col w-full md:w-[48%]">
                            <Label text={"Title"} />
                            <Form.Item
                              {...field}
                              name={[field.name, "title"]}
                              validateTrigger={["onChange", "onBlur"]}
                              rules={[
                                {
                                  required: true,
                                  whitespace: true,
                                  message: "Please input title",
                                },
                              ]}
                            >
                              <CustomInput placeholder="Enter screen title" />
                            </Form.Item>
                          </div>
                          <div className="field flex flex-col w-full md:w-[48%]">
                            <Label text={"Action"} />
                            <Form.Item
                              {...field}
                              name={[field.name, "action"]}
                              validateTrigger={["onChange", "onBlur"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please input action",
                                },
                              ]}
                            >
                              <CustomInput placeholder="Enter action" />
                            </Form.Item>
                          </div>

                          <div className="flex-col w-full md:w-[48%]">
                            <Label text="Order" />
                            <Form.Item
                              name="order"
                              rules={[
                                {
                                  required: true,
                                  message: "Please input order!",
                                },
                              ]}
                            >
                              <Select
                                showSearch
                                allowClear
                                placeholder="Select order"
                              >
                                {Array.from(
                                  { length: 100 },
                                  (_, index) => index + 1
                                ).map((item) => (
                                  <Option value={item} key={item}>
                                    <span>{item}</span>
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </div>

                          <div className="flex-col w-full md:w-[48%]">
                            <Label text="Status" />
                            <Form.Item name="isActive">
                              <div className="switch flex align-center">
                                Active{" "}
                                <Switch className=" mx-3" checked={true} />{" "}
                                Deactive
                              </div>
                            </Form.Item>
                          </div>

                          <div className="flex flex-col w-full">
                            <h3 className=" w-full text-black-500 text-base font-semibold border-b-1 border-gray-900 pb-4">
                              Upload screen image
                            </h3>
                            <Dragger
                              {...props}
                              className=" bg-transparent"
                              accept="image/*,.pdf"
                              beforeUpload={beforeUpload}
                            >
                              <p className=" text-5xl text-blue-500">
                                <InboxOutlined />
                              </p>
                              <p className="ant-upload-text">
                                Click or drag file to this area to upload
                              </p>
                              <p className="ant-upload-hint">
                                Support for a single upload. Strictly prohibited
                                from uploading company data or other banned
                                files.
                              </p>
                            </Dragger>
                          </div>

                          {fields.length > 1 ? (
                            <Button
                              className="flex items-center text-red-500 border-0 bg-transparent mb-3 shadow-none p-0 mt-5"
                              onClick={() => remove(field.name)}
                            >
                              <MinusCircleOutlined />
                              Delete
                            </Button>
                          ) : null}
                        </div>
                      </Fragment>
                    ))}
                    <Form.Item>
                      <div className="flex justify-end">
                        <Button
                          onClick={() => add()}
                          className="border-0 text-blue-500 bg-transparent mb-3 mt-7 shadow-none p-0 text-lg"
                          icon={<PlusOutlined />}
                        >
                          Add
                        </Button>
                      </div>
                      <Form.ErrorList errors={errors} />
                    </Form.Item>
                  </div>
                </Form.Item>
              )}
            </Form.List>
          </div>

          <div className="flex gap-5 justify-end items-center w-full">
            <button
              type="button"
              className="disabled:opacity-50 disabled:cursor-not-allowed rounded-lg bg-white w-[48%] md:w-[24%] py-3 text-center outline-0 cursor-pointer text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
              onClick={() => navigate("/slider-screens")}
            >
              Cancel
            </button>
            <button
              className="disabled:opacity-50 disabled:cursor-not-allowed rounded-lg bg-blue-500 w-[48%] md:w-[24%] py-3 text-center outline-0 cursor-pointer text-sm font-semibold border-[1px] border-blue-500 text-white"
              type="button"
              disabled={!submitForm}
            >
              Submit
            </button>
          </div>
        </Form>
      </div>
    </section>
  );
};

export default AddEditSliderScreen;
